import { useEffect, useRef, useState, useTransition, type ChangeEvent, type FormEvent } from 'react'
import { Link } from 'react-router'
import { Trans, useTranslation } from 'react-i18next'
import { ArrowRight, Building2, CircleCheck, KeyRound, Mail } from 'lucide-react'
import { Alert, Button } from '@agion/ui'
import { AuthSplitLayout } from '@/components/layout/AuthSplitLayout'
import { Field } from '@/components/common/Field'
import { GoogleIcon, MicrosoftIcon } from '@/components/common/ProviderIcons'
import { AuthRequestError, authErrorMessage } from '@/services/auth-errors'
import { verifyWaitlistCode, verifyWaitlistEmail, WaitlistRequestError } from '@/services/waitlistService'
import { buildExternalOAuthLoginUrl, type ExternalOAuthProvider } from '@/utils/auth-continuation'
import { config } from '@/utils/config'
import { hasEmailShape, isBlockedEmailDomain } from '@/utils/validation'

const RESEND_COOLDOWN_SECONDS = 60
// The emailed code's length. Only gates the Continue button; auth-service decides
// whether a code is valid, expired or out of attempts.
const CODE_LENGTH = 6

type Provider = ExternalOAuthProvider

/**
 * Waitlist request flow (design-of-record ui_kits/login/waitlist.html):
 * 1. Request access: work email only, or Google / Microsoft.
 * 2. One more step: an OAuth user whose domain has no organization adds its name.
 * 3. Check your email: the link, or the numeric code from the same email typed here
 *    (PLAN-20260912 slice 55), with resend.
 * The existing-organization branch is decided by auth-service (pending approval).
 */
export const WaitlistPage = () => {
  const { t } = useTranslation()

  const searchParams = new URLSearchParams(window.location.search)
  const hintProvider = searchParams.get('provider') || ''
  const hintEmail = searchParams.get('hint_email') || ''
  const hintOrg = searchParams.get('hint_org') || ''
  const isOAuthRedirect = !!hintProvider && !!hintEmail
  const oauthProvider: Provider = hintProvider === 'google' ? 'google' : 'microsoft'
  const utm = {
    source: searchParams.get('utm_source') || '',
    medium: searchParams.get('utm_medium') || '',
    campaign: searchParams.get('utm_campaign') || '',
  }

  const [email, setEmail] = useState(hintEmail)
  const [emailTouched, setEmailTouched] = useState(false)
  const [orgName, setOrgName] = useState(hintOrg)
  const [displayError, setDisplayError] = useState(
    searchParams.get('error') === 'blocked_domain' ? t('waitlist.blockedDomain') : '',
  )
  const [isPending, startTransition] = useTransition()
  const [emailSent, setEmailSent] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [resendSuccess, setResendSuccess] = useState(false)
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const [isCodePending, startCodeTransition] = useTransition()

  useEffect(() => () => {
    if (cooldownTimer.current) clearInterval(cooldownTimer.current)
  }, [])

  const trimmedEmail = email.trim()
  // The field and the submit gate share this one rule.
  const emailValid = hasEmailShape(trimmedEmail)
  const orgValid = orgName.trim().length >= 2
  const emailError = emailTouched && trimmedEmail && !emailValid ? t('waitlist.emailInvalidWork') : ''

  // A pasted code may carry spaces; the server receives the digits alone.
  const typedCode = code.replace(/\s+/g, '')
  const codeComplete = new RegExp(`^[0-9]{${CODE_LENGTH}}$`).test(typedCode)

  const handleCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value)
    if (codeError) setCodeError('')
  }

  const handleCodeSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!codeComplete || isCodePending) return
    setCodeError('')
    startCodeTransition(async () => {
      try {
        const token = await verifyWaitlistCode(trimmedEmail, typedCode)
        window.location.href = `/waitlist/complete?token=${encodeURIComponent(token)}`
      } catch (err) {
        setCodeError(
          err instanceof AuthRequestError && err.status === 400
            ? t('waitlist.codeInvalid')
            : authErrorMessage(err, t, 'waitlist.codeInvalid'),
        )
      }
    })
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (displayError) setDisplayError('')
  }

  const handleOrgChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOrgName(e.target.value)
    if (displayError) setDisplayError('')
  }

  // One more step: mode=waitlist with the organization name, so the OAuth callback
  // creates the organization through SubmitWithAccount.
  const buildWaitlistOAuthURL = (provider: Provider): string => {
    const params = new URLSearchParams()
    params.set('mode', 'waitlist')
    params.set('org_name', orgName.trim())
    if (utm.source) params.set('utm_source', utm.source)
    if (utm.medium) params.set('utm_medium', utm.medium)
    if (utm.campaign) params.set('utm_campaign', utm.campaign)
    return `${config.authServiceUrl}/oauth/${provider}/login?${params.toString()}`
  }

  const handleContinueOAuth = () => {
    if (!orgValid) return
    window.location.href = buildWaitlistOAuthURL(oauthProvider)
  }

  // Request access with a provider: the standard OAuth entry (no organization yet).
  // auth-service sends a new-organization user back here with provider and hint_email
  // (one more step) and an existing-organization user to pending approval.
  const startProviderRequest = (provider: Provider) => {
    window.location.href = buildExternalOAuthLoginUrl(provider, config.authServiceUrl)
  }

  const startCooldown = () => {
    if (cooldownTimer.current) clearInterval(cooldownTimer.current)
    setResendCooldown(RESEND_COOLDOWN_SECONDS)
    cooldownTimer.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownTimer.current) clearInterval(cooldownTimer.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const requestVerification = async (): Promise<boolean> => {
    try {
      await verifyWaitlistEmail(trimmedEmail)
      return true
    } catch (err) {
      if (err instanceof WaitlistRequestError && err.isBlockedDomain) {
        setDisplayError(t('waitlist.blockedDomain'))
      } else if (err instanceof WaitlistRequestError && err.serverMessage) {
        setDisplayError(err.serverMessage)
      } else {
        setDisplayError(t('waitlist.genericError'))
      }
      return false
    }
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setDisplayError('')
    setEmailTouched(true)
    if (!emailValid) return
    if (isBlockedEmailDomain(trimmedEmail)) {
      setDisplayError(t('waitlist.blockedDomain'))
      return
    }
    startTransition(async () => {
      if (await requestVerification()) {
        setEmailSent(true)
        startCooldown()
      }
    })
  }

  const handleResend = () => {
    if (resendCooldown > 0 || isPending) return
    setResendSuccess(false)
    setDisplayError('')
    startTransition(async () => {
      if (await requestVerification()) {
        setResendSuccess(true)
        startCooldown()
      }
    })
  }

  if (emailSent) {
    return (
      <AuthSplitLayout>
        <div className="lp-state lp-pop">
          <div className="lp-badge lp-badge-success">
            <CircleCheck size={24} aria-hidden="true" />
          </div>
          <h2 className="lp-state-title">{t('waitlist.checkEmailTitle')}</h2>
          <p className="lp-state-msg">
            <Trans
              i18nKey="waitlist.checkEmailMessageRich"
              values={{ email: trimmedEmail }}
              components={{ b: <span className="lp-emph" /> }}
            />
          </p>
          <p className="lp-note">{t('waitlist.checkEmailExpiry')}</p>
          <form onSubmit={handleCodeSubmit} noValidate className="lp-form lp-code-form">
            {codeError && <Alert variant="error">{codeError}</Alert>}
            <Field
              id="verification-code"
              type="text"
              label={t('waitlist.codeLabel')}
              value={code}
              onChange={handleCodeChange}
              leadingIcon={<KeyRound size={16} />}
              valid={codeComplete}
              hint={t('waitlist.codeHint')}
              placeholder={t('waitlist.codePlaceholder')}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={CODE_LENGTH + 4}
              disabled={isCodePending}
            />
            <Button
              type="submit"
              className="lp-btn-primary w-full"
              disabled={!codeComplete || isCodePending}
              isLoading={isCodePending}
            >
              {t('waitlist.codeSubmit')}
              {!isCodePending && <ArrowRight size={16} aria-hidden="true" />}
            </Button>
          </form>
          <hr className="lp-state-divider" />
          {displayError && <Alert variant="error" className="lp-alert">{displayError}</Alert>}
          {resendSuccess && <Alert variant="success" className="lp-alert">{t('waitlist.resendSuccess')}</Alert>}
          <p className="lp-state-prompt">{t('waitlist.didntGetEmail')}</p>
          <Button
            type="button"
            variant="outline"
            className="lp-btn-social"
            onClick={handleResend}
            disabled={resendCooldown > 0 || isPending}
            isLoading={isPending}
          >
            {resendCooldown > 0
              ? t('waitlist.resendCooldown', { seconds: resendCooldown })
              : t('waitlist.resendButton')}
          </Button>
        </div>
      </AuthSplitLayout>
    )
  }

  if (isOAuthRedirect) {
    const ProviderMark = oauthProvider === 'google' ? GoogleIcon : MicrosoftIcon
    return (
      <AuthSplitLayout>
        <div className="lp-heading-block lp-rise lp-rise-2">
          <h2 className="lp-heading">{t('waitlist.oneMoreStepTitle')}</h2>
          <p className="lp-subheading">{t('waitlist.oneMoreStepSubtitle')}</p>
        </div>
        <div className="lp-form lp-rise lp-rise-3">
          {displayError && <Alert variant="error">{displayError}</Alert>}
          <Field
            id="organization"
            type="text"
            label={t('waitlist.orgLabel')}
            value={orgName}
            onChange={handleOrgChange}
            valid={orgValid}
            leadingIcon={<Building2 size={16} />}
            placeholder={t('waitlist.orgPlaceholder')}
            autoComplete="organization"
          />
          <div className="lp-chip" data-testid="signed-in-as">
            <ProviderMark />
            <div>
              <div className="lp-chip-label">{t('waitlist.signedInAs')}</div>
              <div className="lp-chip-email">{hintEmail}</div>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            className="lp-btn-social"
            onClick={handleContinueOAuth}
            disabled={!orgValid}
          >
            <ProviderMark />
            {oauthProvider === 'google' ? t('waitlist.continueWithGoogle') : t('waitlist.continueWithMicrosoft')}
          </Button>
        </div>
      </AuthSplitLayout>
    )
  }

  return (
    <AuthSplitLayout>
      <div className="lp-heading-block lp-rise lp-rise-2">
        <h2 className="lp-heading">{t('waitlist.requestAccessTitle')}</h2>
        <p className="lp-subheading">{t('waitlist.requestAccessSubtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} noValidate className="lp-form lp-rise lp-rise-3">
        {displayError && <Alert variant="error">{displayError}</Alert>}
        <Field
          id="email"
          type="email"
          label={t('waitlist.workEmailLabel')}
          value={email}
          onChange={handleEmailChange}
          onBlur={() => setEmailTouched(true)}
          leadingIcon={<Mail size={16} />}
          error={emailError}
          valid={emailValid}
          required
          autoComplete="email"
          placeholder={t('waitlist.emailPlaceholder')}
          disabled={isPending}
        />
        <Button
          type="submit"
          className="lp-btn-primary w-full"
          disabled={isPending || !emailValid}
          isLoading={isPending}
        >
          {isPending ? t('waitlist.submitting') : t('waitlist.requestAccessButton')}
          {!isPending && <ArrowRight size={16} aria-hidden="true" />}
        </Button>

        <div className="lp-divider">{t('waitlist.orRequestWith')}</div>
        <div className="lp-social-grid">
          <Button
            type="button"
            variant="outline"
            className="lp-btn-social"
            onClick={() => startProviderRequest('google')}
            disabled={isPending}
            aria-label={t('waitlist.googleSignUp')}
          >
            <GoogleIcon />
            {t('social.google')}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="lp-btn-social"
            onClick={() => startProviderRequest('microsoft')}
            disabled={isPending}
            aria-label={t('waitlist.microsoftSignUp')}
          >
            <MicrosoftIcon />
            {t('social.microsoft')}
          </Button>
        </div>
      </form>

      <p className="lp-secondary-cta lp-rise lp-rise-4">
        {t('waitlist.hasAccount')}{' '}
        <Link to="/login">{t('waitlist.signIn')}</Link>
      </p>
    </AuthSplitLayout>
  )
}
